import apiClient from '../api.js';
import { conToken } from '../autorizacion.js';

// Capa de servicios de la oferta de planes económicos por grupos (HU-16).
// Backend: `UNICEN/modules/economico`. Contrato completo:
// UNICEN/modules/economico/docs/oferta-planes.openapi.yaml
//
// Token obligatorio, `{ data }`, errores `{ mensaje, campo?, rechazadas? }` (leer con `errorDeApi`).
//
//   GET    /economico/oferta-planes/opciones                    -> { sedes, tipos_plan }
//   GET    /economico/oferta-planes/por-fila?id_sede&id_carrera&id_plan_estudio -> { planes }
//   GET    /economico/oferta-planes/:id_plan/grupos             -> { plan, grupos }
//   POST   /economico/oferta-planes/:id_plan/grupos { nombre }  -> 201 Grupo
//   PUT    /economico/oferta-planes/:id_plan/grupos/:id_grupo { nombre } -> Grupo
//   DELETE /economico/oferta-planes/:id_plan/grupos/:id_grupo
//   GET    /economico/oferta-planes/:id_plan/filas?id_grupo&id_sede&tipo_plan&buscar&pagina&limite
//          -> { filas, total, pagina, limite }
//   POST   /economico/oferta-planes/:id_plan/filas { id_grupo, filas }               (asignar)
//   POST   /economico/oferta-planes/:id_plan/filas/movimiento/vista-previa { id_grupo, filas }
//   POST   /economico/oferta-planes/:id_plan/filas/movimiento { id_grupo, filas }    (mover)
//   POST   /economico/oferta-planes/:id_plan/filas/retiro { filas }                  (sacar)
//   GET    /economico/oferta-planes/:id_plan/candidatas?id_sede&tipo_plan&buscar&solo_sin_asignar
//   GET    /economico/oferta-planes/:id_plan/historial?pagina&limite
//
// Las filas viajan como { id_sede, id_carrera, id_plan_estudio }. Las sedes del
// filtro van separadas por coma (`id_sede=1,3`).

const resource = '/economico/oferta-planes';

function sinClaves({ id_sede, id_carrera, id_plan_estudio }) {
  return { id_sede, id_carrera, id_plan_estudio };
}

function filtros({ sedes, tipoPlan, buscar, ...resto } = {}) {
  return conToken({
    params: {
      ...resto,
      id_sede: sedes && sedes.length ? sedes.join(',') : undefined,
      tipo_plan: tipoPlan || undefined,
      buscar: buscar || undefined,
    },
  });
}

const useOfertaPlanesApi = {
  opciones() {
    return apiClient.get(`${resource}/opciones`, conToken());
  },

  porFila(fila) {
    return apiClient.get(`${resource}/por-fila`, conToken({ params: sinClaves(fila) }));
  },

  grupos(idPlan) {
    return apiClient.get(`${resource}/${idPlan}/grupos`, conToken());
  },

  crearGrupo(idPlan, nombre) {
    return apiClient.post(`${resource}/${idPlan}/grupos`, { nombre }, conToken());
  },

  renombrarGrupo(idPlan, idGrupo, nombre) {
    return apiClient.put(`${resource}/${idPlan}/grupos/${idGrupo}`, { nombre }, conToken());
  },

  eliminarGrupo(idPlan, idGrupo) {
    return apiClient.delete(`${resource}/${idPlan}/grupos/${idGrupo}`, conToken());
  },

  /** { idGrupo, sedes, tipoPlan, buscar, pagina, limite } */
  filas(idPlan, { idGrupo, ...resto } = {}) {
    return apiClient.get(`${resource}/${idPlan}/filas`, filtros({ ...resto, id_grupo: idGrupo || undefined }));
  },

  /** { sedes, tipoPlan, buscar, soloSinAsignar } */
  candidatas(idPlan, { soloSinAsignar, ...resto } = {}) {
    return apiClient.get(`${resource}/${idPlan}/candidatas`, filtros({ ...resto, solo_sin_asignar: soloSinAsignar }));
  },

  asignar(idPlan, idGrupo, filas) {
    return apiClient.post(`${resource}/${idPlan}/filas`, { id_grupo: idGrupo, filas: filas.map(sinClaves) }, conToken());
  },

  vistaPreviaMovimiento(idPlan, idGrupo, filas) {
    return apiClient.post(`${resource}/${idPlan}/filas/movimiento/vista-previa`,
      { id_grupo: idGrupo, filas: filas.map(sinClaves) }, conToken());
  },

  mover(idPlan, idGrupo, filas) {
    return apiClient.post(`${resource}/${idPlan}/filas/movimiento`, { id_grupo: idGrupo, filas: filas.map(sinClaves) }, conToken());
  },

  retirar(idPlan, filas) {
    return apiClient.post(`${resource}/${idPlan}/filas/retiro`, { filas: filas.map(sinClaves) }, conToken());
  },

  historial(idPlan, { pagina, limite } = {}) {
    return apiClient.get(`${resource}/${idPlan}/historial`, conToken({ params: { pagina, limite } }));
  },
};

export default useOfertaPlanesApi;
